import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { atom, selector } from 'recoil'

import { JobDashboardPublicationStatus } from 'generated/graphql'
import { DEFAULT_JOB_THEME } from 'utils/jobThemeUtils'

export type DashboardItemsWithQueriesLoaded = {
  id: string
  hasErrors?: boolean
  type: string
  headers: {
    x: number
    y: number
    w: number
    h: number
    minH?: number
    minW?: number
  }

  body: any
  isIncomplete?: boolean
  data?: any
  isLoaded: boolean
}

export const dashboardItemsState = atom({
  key: 'dashboardItemsList',
  default: [],
})

// Dashboard Data From Query
export const dashboardState = atom<{
  publicationStatus: JobDashboardPublicationStatus
  dashboard: {
    isLocked: boolean
  }
} | null>({
  key: 'Dashboard',
  default: null,
})

export const isDashboardLockedState = selector({
  key: 'IsDashboardLocked',
  get: ({ get }) => {
    const dashboard = get(dashboardState)
    return dashboard?.dashboard?.isLocked || null
  },
})

export const recoilPublicationStatus = selector<JobDashboardPublicationStatus>({
  key: 'RecoilPublicationStatus',
  get: ({ get }) => {
    const dashboard = get(dashboardState)
    return dashboard?.publicationStatus || JobDashboardPublicationStatus.Draft
  },
})

export const dashboardItemsWithQueriesLoadedState = atom<DashboardItemsWithQueriesLoaded[]>({
  key: 'DashboardItemsWithQueriesLoaded',
  default: [],
})

export const doesDashboardHaveErrorState = selector({
  key: 'doesDashboardHaveErrorState',
  get: ({ get }) => {
    const items = get(dashboardItemsWithQueriesLoadedState)
    const hasErrors = items.some(item => Boolean(item.hasErrors))
    const isAllLoadedAndHasError = items.length > 0 && hasErrors

    return isAllLoadedAndHasError
  },
})

export const doesDashboardHaveIncompleteComponentState = selector({
  key: 'doesDashboardHaveIncompleteComponentState',
  get: ({ get }) => {
    const items = get(dashboardItemsWithQueriesLoadedState)
    const hasIncomplete = items.some(item => {
      const isComponentWithoutEntity = ['text', 'image', 'embed-document', 'icon'].includes(item.type)
      if (isComponentWithoutEntity) {
        const hasData = item.body?.documentPath || item.body?.textContent || item.body?.path || item.body?.typeIcon
        return !hasData
      }
      const isIncomplete = !item?.body?.entityId?.length && !isComponentWithoutEntity
      return isIncomplete
    })

    const isAllLoadedAndHasIncomplete = items.length > 0 && hasIncomplete

    return isAllLoadedAndHasIncomplete
  },
})

export const isAllDashboardItemsWithQueriesLoadedState = selector({
  key: 'IsAllDashboardItemsWithQueriesLoaded',
  get: ({ get }) => {
    const existing_mark = window.performance.getEntriesByName('all_dashboard_items_with_queries_loaded_start')
    if (!existing_mark?.length) {
      window.performance.mark('all_dashboard_items_with_queries_loaded_start')
      datadogRum.addAction('all_dashboard_items_with_queries_loaded_start')
    }

    const items = get(dashboardItemsWithQueriesLoadedState)
    const isAllLoaded = !items.some(item => item.isLoaded === false)
    const IsAllLoadedAndNotEmpty = items.length > 0 && isAllLoaded

    if (IsAllLoadedAndNotEmpty) {
      window.performance.mark('all_dashboard_items_with_queries_loaded_end')
      const measurement = performance.measure(
        'all_dashboard_items_with_queries_loaded',
        'all_dashboard_items_with_queries_loaded_start',
        'all_dashboard_items_with_queries_loaded_end'
      )
      window.performance.clearMarks('all_dashboard_items_with_queries_loaded_start')
      window.performance.clearMarks('all_dashboard_items_with_queries_loaded_end')
      datadogRum.addAction('all_dashboard_items_with_queries_loaded', { duration: measurement.duration })
      datadogLogs.logger.info('all_dashboard_items_with_queries_loaded', { duration: measurement.duration })
      // console.log('measurement', measurement)
    }
    return IsAllLoadedAndNotEmpty
  },
})

export const movingComponentsState = atom({
  key: 'movingComponentsList',
  default: [],
})

export const isDragDisabledState = atom({
  key: 'isDragDisabledState',
  default: true,
})

export const isFullScreenState = atom({
  key: 'isFullScreenState',
  default: false,
})

export const dashboardTheme = atom({
  key: 'dashboardThemeState',
  default: DEFAULT_JOB_THEME,
})

export const territoriesTree = atom({
  key: 'territoriesTree',
  default: null,
})

export const territoriesTreeObject = atom({
  key: 'territoriesTreeObject',
  default: null,
})

export const territoriesLanguageTreeObject = atom({
  key: 'territoriesLanguageTreeObject',
  default: {
    por: null,
    eng: null,
    spa: null,
    all: null,
  },
})

export const globalTopicsList = atom({
  key: 'globalTopicsList',
  default: null,
})
